import { Moment } from 'moment'

import { IntervalUnit } from './Bundle'
import { SegmentTarget } from './Campaign'
import { PostalCodeTarget } from './Geo'
import { MarketAnalysis } from './MarketAnalysis'
import { Persona } from './Persona'

export interface AIPlan {
  id: string
  blueprintId: string
  organizationId: string
  accountId: string
  companyName: string
  companyDomain: string
  name: string
  status: AIPlanStatus

  aiRecommendedBudget: boolean
  monthlyBudget?: number
  aiRecommendedChannels: boolean
  mustIncludeChannels: string[]
  mustExcludeChannels: string[]
  personaMode: string
  selectedPersonas: string[]
  planDurationInMonths: number
  planStartDate: string
  userProvidedInsights?: string

  aiPlanSummary: string
  aiPlanDetails: string
  aiAudienceSummary: string
  aiAudienceDetails: string
  aiGeoSummary: string
  aiGeoDetails: string
  aiSeasonalitySummary: string
  aiSeasonalityDetails: string
  aiUsedMonthlyBudgetMin: number
  aiUsedTargetMonthlyBudget: number
  aiUsedMonthlyBudgetMax: number
  aiUsedSingleBudgetMax: number
  aiUsedStartDate: string
  aiUsedDurationInMonths: number

  planSummary: string
  planDetails: string
  audienceSummary: string
  audienceDetails: string
  geoSummary: string
  geoDetails: string
  seasonalitySummary: string
  seasonalityDetails: string

  personas: Persona[]
  aiPersonas: Persona[]
  marketAnalysis: MarketAnalysis
  aiMarketAnalysis: MarketAnalysis

  publicId?: string
  clientPreviewShowExecutiveSummary?: boolean | null
  clientPreviewShowMarketAnalysis?: boolean | null
  clientPreviewShowPersonas?: boolean | null
  clientPreviewShowMediaPlan?: boolean | null
  clientPreviewShowProjections?: boolean | null

  aiPlanStubId: string
  masterCampaignId?: string
  copyToDspDate?: string

  highlightedDomainIds: string[]
  internetMarginOfError: number
  tvMarginOfError: number
  billboardMarginOfError: number
  audioMarginOfError: number
  tvNetworkDistribution?: Record<string, number>

  created: string
  createdBy: string
  updated: string
  updatedBy: string
  isHidden?: boolean
}

export enum AIPlanStatus {
  Processing = 'Processing',
  Ready = 'Ready',
  CopiedToDsp = 'CopiedToDsp',
  Deleted = 'Deleted',
  Disapproved = 'Disapproved'
}

export interface AIPlanStub {
  id: string
  accountId: string
  organizationId: string
  aiPlanId: string
  name: string
  campaignBuilders: AIPlanCampaignBuilder[]
  aiCampaignBuilders: AIPlanCampaignBuilder[]
  created: string
  createdBy: string
  updated: string
  updatedBy: string
}

export interface AIPlanCampaignBuilder {
  id: string
  name: string
  shortSummary: string
  longSummary: string
  audienceSummary: string
  audienceDetails: string
  geoSummary: string
  geoDetails: string
  seasonalitySummary: string
  seasonalityDetails: string
  campaignType: AIPlanCampaignBuilderType
  isOffAdCritter: boolean
  offAdCritterName: string
  offAdCritterColor: string
  intervalBudget: number
  retargetSite: boolean
  isTotalBudget: boolean
  startDate: Moment
  endDate?: Moment
  intervalUnit?: IntervalUnit
  intervalFrequency?: number
  intervalDuration?: number
  segmentTargets: SegmentTarget[]
  audienceSegmentLooksUp: string[]
  targetingType?: AIPlanLocationTargetType
  regions: string[]
  dmas: string[]
  cities: string[]
  zips: PostalCodeTarget[]
  copiedToDspDate?: string
  dspCampaignId?: string
  copyStatus?: AIPlanCampaignCopyStatus
  minCpm?: number
  cpm?: number
  maxCpm?: number
  marginOfError?: number
  includeInProjections: boolean
  includeInGeoHeatmap: boolean
  keywords?: string[]
  keywordDescription?: string
  cpc?: number
  minCpc?: number
  maxCpc?: number
  segmentDescriptions?: string[]
}

export enum AIPlanCampaignBuilderType {
  Internet = 'Internet',
  Billboard = 'Billboard',
  TV = 'TV',
  Audio = 'Audio',
  OffAdCritter = 'OffAdCritter'
}

export enum AIPlanLocationTargetType {
  Region = 'Region',
  Country = 'Country',
  DMA = 'DMA',
  City = 'City',
  Zip = 'Zip',
  Map = 'Map',
  Address = 'Address',
  Uploaded = 'Uploaded',
  USA = 'Usa'
}

export enum AIPlanCampaignCopyStatus {
  NotCopied = 'NotCopied',
  Copied = 'Copied'
}

export interface AIPlanCustomSection {
  id: string
  title: string
  showTitle?: boolean
  content: string
  hideAll?: boolean
  createdBy: string
  createdAt: Moment
  updatedBy: string
  updatedAt: Moment
}
